import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import FacebookLoginLib from '@greatsumini/react-facebook-login';
import AppleLoginLib from 'react-apple-login';

// Helper to resolve the actual component from various module formats (CJS/ESM/Interops)
const resolveComponent = (mod: any) => {
    if (mod && mod.default) {
        return mod.default;
    }
    return mod;
};

const FacebookLogin = resolveComponent(FacebookLoginLib);
const AppleLogin = resolveComponent(AppleLoginLib);



interface SocialLoginButtonsProps {
    onSocialLogin: (provider: string, token: string) => Promise<void>;
    isLoading: boolean;
}

export const SocialLoginButtons = ({ onSocialLogin, isLoading }: SocialLoginButtonsProps) => {
    const { t } = useTranslation('common');
    const CDN_URL = ((window as any).__DASHBAL_CONFIG__?.cdnUrl || 'https://cdn.dashbal.com').replace(/\/$/, '');

    // --- Configuration (env vars) ---
    // Note: process.env is replaced by esbuild with actual string values
    const GOOGLE_CLIENT_ID = window.__DASHBAL_CONFIG__?.googleClientId || 
        (typeof process !== 'undefined' ? process.env.GOOGLE_CLIENT_ID : '') || '';
    const FACEBOOK_APP_ID = window.__DASHBAL_CONFIG__?.facebookAppId || 
        (typeof process !== 'undefined' ? process.env.FACEBOOK_APP_ID : '') || '';
    const FACEBOOK_CONFIG_ID = window.__DASHBAL_CONFIG__?.facebookConfigId || '';
    const APPLE_CLIENT_ID = window.__DASHBAL_CONFIG__?.appleClientId || 
        (typeof process !== 'undefined' ? process.env.APPLE_CLIENT_ID : '') || '';
    const APPLE_REDIRECT_URI = (typeof process !== 'undefined' ? process.env.APPLE_REDIRECT_URI : '') || window.location.origin;
    
    // Check config first (injected by server.js), then fallback to build-time ENV
    const TIKTOK_CLIENT_KEY = window.__DASHBAL_CONFIG__?.tiktokClientKey || 
        (typeof process !== 'undefined' ? process.env.TIKTOK_CLIENT_KEY : '') || '';

    // --- TikTok ---
    const loginTikTok = () => {
        const width = 500;
        const height = 660;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
            `${window.__DASHBAL_CONFIG__?.apiBaseUrl}/auth/tiktok/initiate`,
            'TikTok Login',
            `width=${width},height=${height},left=${left},top=${top}`
        );

        if (popup) {
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                
                if (event.data?.type === 'oauth-success' && event.data?.provider === 'tiktok') {
                    if (event.data.token) {
                        onSocialLogin('tiktok', event.data.token);
                    }
                    window.removeEventListener('message', handleMessage);
                } else if (event.data?.type === 'oauth-error') {
                    console.error('TikTok Login Error', event.data.error, event.data.errorDescription);
                    window.removeEventListener('message', handleMessage);
                }
            };

            window.addEventListener('message', handleMessage);
        }
    };

    // --- Google ---
    const loginGoogle = useGoogleLogin({
        onSuccess: tokenResponse => {
            if (window.__DASHBAL_CONFIG__?.enableDebug) {
                console.log("Google Login SUCCESS:", tokenResponse);
            }
            // We use the access_token. The backend has been updated to verify it via userinfo endpoint.
            if (tokenResponse.access_token) {
                onSocialLogin('google', tokenResponse.access_token);
            }
        },
        onError: (error) => {
            console.error("Google Login Status FAILED:", error);
        },
        // Explicitly set these to avoid mismatch if the library detects wrong origin
        hint: undefined,
        flow: 'implicit'
    });

    // --- Facebook ---
    // Using @greatsumini/react-facebook-login
    const handleFacebookSuccess = (response: any) => {
        console.log("Facebook Login Success", response);
        if (response.accessToken) {
            onSocialLogin('facebook', response.accessToken);
        }
    };

    const handleFacebookFail = (error: any) => {
        console.error("Facebook Login Failed", error);
    };

    // --- Apple ---
    const responseApple = (response: any) => {
        console.log("Apple Response", response);
        if (!response.error) {
            // Apple returns object with authorization: { id_token, code, ... }
            if (response.authorization && response.authorization.id_token) {
                onSocialLogin('apple', response.authorization.id_token);
            }
        } else {
            console.error("Apple Login Failed", response.error);
        }
    }

    return (
        <div className="flex flex-col gap-3 w-full">
            {/* GOOGLE */}
            {GOOGLE_CLIENT_ID ? (
                <button
                    type="button"
                    onClick={() => {
                        if (window.__DASHBAL_CONFIG__?.enableDebug) {
                            console.log("Initiating Google Login with Client ID:", GOOGLE_CLIENT_ID);
                        }
                        loginGoogle();
                    }}
                    disabled={isLoading}
                    className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    <div className="absolute left-4">
                        <img src={`${CDN_URL}/logos/channel_logos/google/google_primary.svg`} alt="Google" className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                        {t('auth.continueWithGoogle', 'Continue with Google')}
                    </span>
                </button>
            ) : null}

            {/* APPLE */}
            {APPLE_CLIENT_ID ? (
                <AppleLogin
                    clientId={APPLE_CLIENT_ID}
                    redirectURI={APPLE_REDIRECT_URI}
                    usePopup={true}
                    callback={responseApple}
                    scope="email name"
                    responseMode="query"
                    render={(renderProps: any) => (
                        <button
                            type="button"
                            onClick={renderProps.onClick}
                            disabled={isLoading || renderProps.disabled}
                            className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                            <div className="absolute left-4">
                                <img src={`${CDN_URL}/logos/channel_logos/apple/apple_primary.png`} alt="Apple" className="w-5 h-5 dark:invert" />
                            </div>
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                {t('auth.continueWithApple', 'Continue with Apple')}
                            </span>
                        </button>
                    )}
                />
            ) : null}

            {/* FACEBOOK */}
            {FACEBOOK_APP_ID ? (
                <FacebookLogin
                    appId={FACEBOOK_APP_ID}
                    onSuccess={handleFacebookSuccess}
                    onFail={handleFacebookFail}
                    // When using config_id, permissions are defined in the Meta dashboard configuration.
                    // If no config_id is provided, we fallback to standard scope-based login.
                    scope={FACEBOOK_CONFIG_ID ? undefined : "public_profile,email"}
                    fields="name,email,picture"
                    initParams={{
                        version: 'v22.0',
                        xfbml: true,
                        cookie: true
                    }}
                    loginOptions={{
                        config_id: FACEBOOK_CONFIG_ID || undefined,
                        override_default_response_type: true,
                        response_type: 'token'
                    }}
                    render={({ onClick }: any) => (
                        <button
                            type="button"
                            onClick={onClick}
                            disabled={isLoading}
                            className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                            <div className="absolute left-4">
                                <img src={`${CDN_URL}/logos/channel_logos/facebook/facebook_primary.png`} alt="Facebook" className="w-5 h-5" />
                            </div>
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                {t('auth.continueWithFacebook', 'Continue with Facebook')}
                            </span>
                        </button>
                    )}
                />
            ) : null}

            {/* TIKTOK */}
            {TIKTOK_CLIENT_KEY ? (
                <button
                    type="button"
                    onClick={loginTikTok}
                    disabled={isLoading}
                    className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    <div className="absolute left-4">
                        <img src={`${CDN_URL}/logos/channel_logos/tiktok/tiktok_primary.png`} alt="TikTok" className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                        {t('auth.continueWithTikTok', 'Continue with TikTok')}
                    </span>
                </button>
            ) : null}
        </div>
    );
};
